import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Navigate, NavLink, Route, Routes } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { ALL_COUNTRIES, can, PROVIDER_CATALOG, PROVIDER_KINDS, ROLE_INFO, STUDIO_ROLES, type ProviderField, type ProviderKind } from "@jange/shared";
import { admin, api, ApiError, elevation, type AdminCountry, type CountryBilling, type ProviderKindView, type ProviderSettingView, type Role, type TeamMember } from "../lib/api";
import { useStudio } from "../lib/context";
import { AccessTab } from "./AccessTab";
import { ReferenceTab } from "./ReferenceTab";
import { Activity, Bot, Boxes, CheckCircle2, Circle, Cpu, Database, History, KeyRound, LifeBuoy, MessageCircle, MessageSquare, Mic, Plus, Send, Server, ShieldCheck, Volume2, Wallet, XCircle } from "lucide-react";
import { Avatar, Drawer, Empty, ErrorBox, errorMessage, Field, flagOf, fmtDate, Loading, PageHead, timeAgo } from "../components/ui";

/**
 * Partie système du studio : réservée aux rôles Administrateur et Suivi technique.
 * Double authentification obligatoire (activation au premier passage, puis code toutes les 15 min).
 */
export function SystemPage() {
  const me = useQuery({ queryKey: ["admin-me"], queryFn: admin.me });
  if (me.isLoading) return <Loading />;
  if (me.error) return <ErrorBox error={me.error} onRetry={() => me.refetch()} />;
  if (!me.data?.system) return <Navigate to="/" replace />;
  const perms = me.data.permissions;
  // Les rubriques sont dans la barre latérale (groupe « Administration ») : une page par rubrique.
  return (
    <TwoFactorGate enabled={me.data.totpEnabled} onEnabled={() => me.refetch()}>
      <Routes>
        <Route index element={<StatusTab canImport={perms.includes("bank.manage")} />} />
        <Route path="pays" element={<CountriesTab canEdit={perms.includes("countries.manage")} />} />
        <Route path="formules" element={<><PageHead kicker="Administration" title="Formules" lead="Ce que chaque situation débloque dans l'app : sans abonnement, essai, délai de grâce, chaque formule." /><AccessTab canEdit={perms.includes("countries.manage")} /></>} />
        <Route path="fournisseurs" element={<ProvidersTab canEdit={perms.includes("providers.edit")} />} />
        {perms.includes("reference.manage") && <Route path="referentiel" element={<ReferenceTab />} />}
        {perms.includes("team.view") && <Route path="equipe" element={<TeamTab />} />}
        {perms.includes("team.view") && <Route path="journal" element={<AuditTab />} />}
        <Route path="*" element={<Navigate to="/systeme" replace />} />
      </Routes>
    </TwoFactorGate>
  );
}

// ---------------------------------------------------------------------------
// Double authentification
// ---------------------------------------------------------------------------
function TwoFactorGate({ enabled, onEnabled, children }: { enabled: boolean; onEnabled: () => void; children: ReactNode }) {
  const [, force] = useState(0);
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState<{ secret: string; uri: string; qr: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();

  // La session renforcée expire : on redemande le code sans perdre la page.
  useEffect(() => {
    const until = elevation.until();
    if (!until) return;
    const t = setTimeout(() => force((n) => n + 1), Math.max(1000, until - Date.now()));
    return () => clearTimeout(t);
  }, [setup, code]);

  if (enabled && elevation.get()) return <>{children}</>;

  async function start() {
    setError(null); setBusy(true);
    try { const s = await admin.totpSetup(); setSetup({ ...s, qr: await QRCode.toDataURL(s.uri, { margin: 1, width: 200 }) }); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || code.length !== 6) return;
    setBusy(true); setError(null);
    try {
      elevation.set(enabled ? await admin.totpVerify(code) : await admin.totpEnable(code));
      setCode("");
      if (!enabled) onEnabled();
      void qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith("admin-") });
      force((n) => n + 1);
    } catch (err) { setError(err instanceof ApiError && err.code === "totp_locked" ? "Trop d'essais : réessayez dans 15 minutes." : errorMessage(err)); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ maxWidth: 480, margin: "4vh auto 0" }}>
      <div className="stack" style={{ alignItems: "center", textAlign: "center", marginBottom: 18, gap: 12 }}>
        <span className="icon-tile" style={{ width: 52, height: 52, borderRadius: 16 }}><ShieldCheck size={24} /></span>
        <PageHead kicker="Administration" title="Double authentification" />
      </div>
      <form className="card stack appear" style={{ gap: 14, padding: 24 }} onSubmit={submit}>
        {enabled ? (
          <p className="muted" style={{ margin: 0 }}>Saisissez le code à 6 chiffres de votre application d'authentification. Il reste valable pour 15 minutes de travail.</p>
        ) : !setup ? (
          <>
            <p style={{ margin: 0 }}>La partie système donne accès aux clés des fournisseurs et à l'équipe. Elle est protégée par un code à usage unique, généré sur votre téléphone.</p>
            <p className="muted small" style={{ margin: 0 }}>Installez Google Authenticator, Microsoft Authenticator, Aegis ou 1Password, puis continuez.</p>
            <button type="button" className="btn" onClick={start} disabled={busy}>Activer la double authentification</button>
          </>
        ) : (
          <>
            <p style={{ margin: 0 }}>Scannez ce code avec l'application, puis saisissez le code affiché.</p>
            <img src={setup.qr} alt="QR code à scanner avec l'application d'authentification" width={200} height={200} style={{ alignSelf: "center", borderRadius: 8, border: "1px solid var(--border)" }} />
            <Field label="Ou saisissez cette clé à la main"><code className="mono" style={{ wordBreak: "break-all" }}>{setup.secret.replace(/(.{4})/g, "$1 ").trim()}</code></Field>
          </>
        )}
        {(enabled || setup) && (
          <Field label="Code à 6 chiffres">
            <input className="input mono" inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} autoFocus aria-label="Code à 6 chiffres" />
          </Field>
        )}
        {error && <div className="alert err" role="alert">{error}</div>}
        {(enabled || setup) && <button className="btn" disabled={busy || code.length !== 6}>{busy ? "…" : "Valider"}</button>}
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// État
// ---------------------------------------------------------------------------
const SOURCE: Record<string, { label: string; cls: string }> = {
  db: { label: "Studio", cls: "b-VALIDATED" },
  env: { label: "Serveur", cls: "b-IN_REVIEW" },
  none: { label: "Non configuré", cls: "b-MISSING" },
};

function StatusTab({ canImport }: { canImport: boolean }) {
  const s = useQuery({ queryKey: ["admin-status"], queryFn: admin.status, refetchInterval: 30_000 });
  if (s.isLoading) return <Loading />;
  if (s.error || !s.data) return <ErrorBox error={s.error} onRetry={() => s.refetch()} />;
  const d = s.data;
  const tile = (icon: ReactNode, label: string, ok: boolean | null, sub: string) => (
    <div className="card svc">
      <span className={`icon-tile ${ok === false ? "bad" : ok === null ? "ink" : "ok"}`}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <div className="tiny muted strong" style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
        <div className="state" style={{ color: ok === false ? "var(--bad)" : ok === null ? "var(--text-3)" : "var(--ok)" }}>{ok === false ? "En panne" : ok === null ? "Non configuré" : "Opérationnel"}</div>
        <div className="tiny muted">{sub}</div>
      </div>
    </div>
  );
  const allOk = d.api.ok && d.database.ok && d.redis.ok !== false;
  return (
    <div className="stack" style={{ gap: 18 }}>
      <PageHead kicker="Administration" title="État du service" lead="Santé de l'API et de ses dépendances, actualisée toutes les 30 secondes.">
        <span className={`badge ${allOk ? "b-VALIDATED" : "b-REJECTED"}`}>{allOk ? "Tout fonctionne" : "Incident en cours"}</span>
      </PageHead>
      <div className="grid stats">
        {tile(<Server size={18} />, "API", d.api.ok, `${d.api.env} · en marche depuis ${Math.round(d.api.uptimeSec / 60)} min`)}
        {tile(<Database size={18} />, "Base de données", d.database.ok, `${d.database.ms} ms`)}
        {tile(<Cpu size={18} />, "Redis", d.redis.ok, d.redis.note ?? "files, temps réel, limites")}
        {tile(<KeyRound size={18} />, "Clé maître", true, `empreinte ${d.keyId}`)}
      </div>
      {canImport && <BankImportCard />}
      <section className="card pad-0">
        <div className="section-title" style={{ padding: "18px 20px 0" }}>
          <div style={{ flex: 1 }}><h2>Fournisseurs par pays</h2><p>Le réglage d'un pays passe devant celui de « tous les pays ».</p></div>
          <div className="legend"><span><i style={{ background: "var(--ok)" }} />Studio (chiffré)</span><span><i style={{ background: "var(--amber)" }} />Serveur (variable d'environnement)</span><span><i style={{ background: "var(--border-strong)" }} />Non configuré</span></div>
        </div>
        <div className="table-wrap">
          <table className="t matrix">
            <thead><tr><th>Service</th>{d.providers.map((p) => <th key={p.scope}>{p.scope === ALL_COUNTRIES ? "Tous les pays" : `${flagOf(p.scope)} ${p.name}`}</th>)}</tr></thead>
            <tbody>
              {PROVIDER_KINDS.map((k) => (
                <tr key={k}>
                  <td><span className="row" style={{ gap: 10 }}><span className="icon-tile ink" style={{ width: 30, height: 30, borderRadius: 9 }}>{KIND_ICON[k]}</span><b>{PROVIDER_CATALOG[k].label}</b></span></td>
                  {d.providers.map((p) => {
                    const c = p.kinds.find((x) => x.kind === k);
                    if (!c) return <td key={p.scope} />;
                    return (
                      <td key={p.scope}>
                        <span className={`badge ${SOURCE[c.source]!.cls}`}>{SOURCE[c.source]!.label}{c.version ? ` v${c.version}` : ""}</span>
                        {c.provider && <div className="tiny muted" style={{ marginTop: 4 }}>{c.provider}{c.scope && c.scope !== p.scope ? " (tous pays)" : ""}</div>}
                        {c.lastTest && <div className="tiny" style={{ color: c.lastTest.ok ? "var(--ok)" : "var(--bad)" }}>{c.lastTest.ok ? "Test réussi" : "Test échoué"} · {timeAgo(c.lastTest.at)}</div>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

/** Import en série de la banque 3D : état du manifeste et lancement (traité par le worker). */
function BankImportCard() {
  const { toast } = useStudio();
  const [busy, setBusy] = useState(false);
  const st = useQuery({
    queryKey: ["admin-bank"], queryFn: admin.bankStatus,
    refetchInterval: (q) => (q.state.data?.entries.some((e) => e.status === "QUEUED" || e.status === "RUNNING") ? 4000 : false),
  });
  if (st.isLoading) return <Loading lines={2} />;
  if (st.error || !st.data) return <ErrorBox error={st.error} onRetry={() => st.refetch()} />;
  const e = st.data.entries;
  const done = e.filter((x) => x.status === "DONE" && !x.stale).length;
  const failed = e.filter((x) => x.status === "FAILED");
  const working = e.filter((x) => x.status === "QUEUED" || x.status === "RUNNING").length;
  const todo = e.length - done - working;
  const run = async (force = false) => {
    setBusy(true);
    try { const r = await admin.bankImport({ force }); toast(r.queued ? `${r.queued} modèle${r.queued > 1 ? "s" : ""} en cours d'import` : "Tout est déjà à jour"); void st.refetch(); }
    catch (err) { toast(errorMessage(err), "err"); }
    finally { setBusy(false); }
  };
  return (
    <section className="card stack" style={{ gap: 12 }} aria-labelledby="h-bank">
      <div className="row wrap">
        <span className="icon-tile"><Boxes size={18} /></span>
        <div style={{ flex: 1, minWidth: 220 }}>
          <h2 id="h-bank">Banque 3D</h2>
          <p className="small muted">{done} / {e.length} modèles importés{working ? ` · ${working} en cours` : ""}{failed.length ? ` · ${failed.length} en échec` : ""}. Téléchargement, allègement et rangement par le worker.</p>
        </div>
        <button className="btn" disabled={busy || todo === 0} onClick={() => run(false)}>{todo ? `Importer ${todo} modèle${todo > 1 ? "s" : ""}` : "À jour"}</button>
      </div>
      <div className="progress lg"><span className="p-ok" style={{ flexGrow: done }} /><span className="p-review" style={{ flexGrow: working }} /><span style={{ flexGrow: Math.max(0, e.length - done - working) }} /></div>
      {failed.length > 0 && (
        <div className="alert err small" style={{ flexDirection: "column", alignItems: "stretch", gap: 4 }}>
          {failed.slice(0, 6).map((x) => <div key={x.slug}><b>{x.title}</b> : {x.error}</div>)}
          {failed.length > 6 && <div>… et {failed.length - 6} autres</div>}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Fournisseurs
// ---------------------------------------------------------------------------
const STATUS_LABEL = { ACTIVE: "Actif", INACTIVE: "Enregistré", SUPERSEDED: "Ancien" } as const;
const KIND_ICON: Record<ProviderKind, ReactNode> = {
  LLM: <Bot size={18} />, LLM_FALLBACK: <LifeBuoy size={18} />, STT: <Mic size={18} />, TTS: <Volume2 size={18} />,
  SMS: <MessageSquare size={18} />, WHATSAPP: <MessageCircle size={18} />, TELEGRAM: <Send size={18} />,
  PAYMENT_WAVE: <Wallet size={18} />, PAYMENT_ORANGE_MONEY: <Wallet size={18} />, PAYMENT_BICTORYS: <Wallet size={18} />,
};
const GROUPS: { title: string; lead: string; kinds: ProviderKind[] }[] = [
  { title: "Intelligence artificielle", lead: "Tuteur, questions à l'oral et voix des réponses.", kinds: ["LLM", "LLM_FALLBACK", "STT", "TTS"] },
  { title: "Messages", lead: "Codes de connexion, bilans et alertes aux parents.", kinds: ["SMS", "TELEGRAM", "WHATSAPP"] },
  { title: "Paiement", lead: "Encaissement des abonnements.", kinds: ["PAYMENT_WAVE", "PAYMENT_ORANGE_MONEY", "PAYMENT_BICTORYS"] },
];

function ProvidersTab({ canEdit }: { canEdit: boolean }) {
  const countries = useQuery({ queryKey: ["countries"], queryFn: api.countries, staleTime: 60 * 60_000 });
  const [scope, setScope] = useState<string>(ALL_COUNTRIES);
  const [open, setOpen] = useState<ProviderKind | null>(null);
  const list = useQuery({ queryKey: ["admin-providers", scope], queryFn: () => admin.providers(scope) });
  const byKind = new Map((list.data ?? []).map((k) => [k.kind, k]));
  const known = new Set(GROUPS.flatMap((g) => g.kinds));
  const others = (list.data ?? []).filter((k) => !known.has(k.kind)).map((k) => k.kind);
  const groups = others.length ? [...GROUPS, { title: "Autres", lead: "", kinds: others }] : GROUPS;
  const current = open ? byKind.get(open) : undefined;
  return (
    <div className="stack" style={{ gap: 18 }}>
      <PageHead kicker="Administration" title="Fournisseurs" lead="Chaque service se règle, se teste puis s'active. Les clés sont chiffrées et ne sont jamais réaffichées." />
      <div className="row wrap">
        <div className="seg" role="group" aria-label="Portée du réglage">
          <button type="button" aria-pressed={scope === ALL_COUNTRIES} onClick={() => setScope(ALL_COUNTRIES)}>Tous les pays</button>
          {countries.data?.map((c) => <button key={c.id} type="button" aria-pressed={scope === c.id} onClick={() => setScope(c.id)}>{flagOf(c.id)} {c.name}</button>)}
        </div>
        <span className="spacer" />
        {!canEdit && <span className="alert warn small">Suivi technique : tests possibles, clés invisibles, modifications réservées à l'administrateur.</span>}
      </div>
      {list.isLoading ? <Loading /> : list.error ? <ErrorBox error={list.error} onRetry={() => list.refetch()} /> : groups.map((g) => (
        <section key={g.title} className="stack" style={{ gap: 10 }}>
          <div className="section-title" style={{ marginBottom: 0 }}><h2>{g.title}</h2>{g.lead && <p>{g.lead}</p>}</div>
          <div className="tiles">
            {g.kinds.map((kind) => { const k = byKind.get(kind); return k ? <ProviderTile key={kind} k={k} scope={scope} canEdit={canEdit} onOpen={() => setOpen(kind)} /> : null; })}
          </div>
        </section>
      ))}
      {current && <ProviderDrawer k={current} scope={scope} scopeName={countries.data?.find((c) => c.id === scope)?.name ?? "Tous les pays"} canEdit={canEdit} onClose={() => setOpen(null)} />}
    </div>
  );
}

function useProviderActions() {
  const { toast } = useStudio();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith("admin-") });
  const test = useMutation({ mutationFn: admin.testProvider, onSuccess: (r) => { toast(r.ok ? `Test réussi : ${r.message}` : `Test échoué : ${r.message}`, r.ok ? "ok" : "err"); void refresh(); }, onError: (e) => toast(errorMessage(e), "err") });
  const activate = useMutation({ mutationFn: admin.activateProvider, onSuccess: () => { toast("Activé : pris en compte sur toutes les instances en quelques secondes"); void refresh(); }, onError: (e) => toast(errorMessage(e), "err") });
  const deactivate = useMutation({ mutationFn: admin.deactivateProvider, onSuccess: () => { toast("Désactivé : retour au réglage par défaut"); void refresh(); }, onError: (e) => toast(errorMessage(e), "err") });
  return { test, activate, deactivate, refresh };
}

function effectiveLabel(k: ProviderKindView, scope: string) {
  return k.effective ? `${SOURCE[k.effective.source]!.label}${k.effective.version ? ` v${k.effective.version}` : ""} · ${k.effective.provider}${k.effective.scope !== scope ? " (tous pays)" : ""}` : "Non configuré";
}

function ProviderTile({ k, scope, canEdit, onOpen }: { k: ProviderKindView; scope: string; canEdit: boolean; onOpen: () => void }) {
  const cat = PROVIDER_CATALOG[k.kind];
  const { test } = useProviderActions();
  const latest = k.history[0];
  const pending = latest && latest.status === "INACTIVE" ? latest : null;
  const lastTest = k.active?.test;
  return (
    <article className="card tile interactive appear" aria-label={cat.label}>
      <div className="row" style={{ alignItems: "flex-start" }}>
        <span className={`icon-tile ${k.effective ? "" : "ink"}`}>{KIND_ICON[k.kind]}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontSize: 15 }}>{cat.label}</h3>
          <span className={`badge ${SOURCE[k.effective?.source ?? "none"]!.cls}`} style={{ marginTop: 6 }}>{effectiveLabel(k, scope)}</span>
        </div>
      </div>
      <p className="desc" title={cat.description}>{cat.description}</p>
      {pending && <div className="alert warn small" style={{ padding: "7px 10px" }}>Version {pending.version} enregistrée, à activer</div>}
      <div className="row" style={{ gap: 8, marginTop: "auto" }}>
        {lastTest
          ? <span className="tiny row" style={{ gap: 5, color: lastTest.ok ? "var(--ok)" : "var(--bad)", fontWeight: 700 }}>{lastTest.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}{lastTest.ok ? "Test réussi" : "Test échoué"} · {timeAgo(lastTest.at)}</span>
          : <span className="tiny muted row" style={{ gap: 5 }}><Circle size={12} />{k.active ? "Jamais testé" : "Aucun réglage dans le studio"}</span>}
        <span className="spacer" />
        {k.active && <button className="btn sm ghost" onClick={() => test.mutate(k.active!.id)} disabled={test.isPending}>{test.isPending ? "Test…" : "Tester"}</button>}
        <button className="btn sm" onClick={onOpen} aria-label={`${canEdit ? (k.active || pending ? "Gérer" : "Configurer") : "Voir"} : ${cat.label}`}>{canEdit ? (k.active || pending ? "Gérer" : "Configurer") : "Voir"}</button>
      </div>
    </article>
  );
}

function ProviderDrawer({ k, scope, scopeName, canEdit, onClose }: { k: ProviderKindView; scope: string; scopeName: string; canEdit: boolean; onClose: () => void }) {
  const cat = PROVIDER_CATALOG[k.kind];
  const { test, activate, deactivate, refresh } = useProviderActions();
  const latest = k.history[0];
  const pending = latest && latest.status === "INACTIVE" ? latest : null;
  const [editing, setEditing] = useState(!k.active && !pending && canEdit);
  const [showHistory, setShowHistory] = useState(false);
  return (
    <Drawer title={cat.label} wide icon={<span className="icon-tile">{KIND_ICON[k.kind]}</span>} onClose={onClose}
      subtitle={<>{scope === ALL_COUNTRIES ? "Tous les pays" : `${flagOf(scope)} ${scopeName}`} · <span className={`badge ${SOURCE[k.effective?.source ?? "none"]!.cls}`}>{effectiveLabel(k, scope)}</span></>}>
      <p className="dim">{cat.description}</p>
      {k.active && <SettingLine s={k.active} kind={k.kind} />}
      {pending && (
        <div className="alert warn" style={{ alignItems: "center" }}>
          <span style={{ flex: 1 }}>Version {pending.version} enregistrée, pas encore active.{pending.test ? (pending.test.ok ? " Test réussi." : " Dernier test échoué.") : " Testez-la avant de l'activer."}</span>
          <button className="btn sm ghost" onClick={() => test.mutate(pending.id)} disabled={test.isPending}>{test.isPending ? "Test…" : "Tester"}</button>
          {canEdit && <button className="btn sm" onClick={() => activate.mutate(pending.id)} disabled={activate.isPending}>Activer</button>}
        </div>
      )}
      <div className="row wrap" style={{ gap: 8 }}>
        {k.active && <button className="btn sm ghost" onClick={() => test.mutate(k.active!.id)} disabled={test.isPending}><Activity size={14} />{test.isPending ? "Test…" : "Tester l'actif"}</button>}
        {canEdit && !editing && <button className="btn sm" onClick={() => setEditing(true)}><Plus size={14} />Nouvelle version</button>}
        <span className="spacer" />
        {canEdit && k.active && <button className="btn sm danger" onClick={() => { if (confirm("Désactiver ce réglage ? Le réglage par défaut (tous pays ou serveur) reprendra la main.")) deactivate.mutate(k.active!.id); }}>Désactiver</button>}
      </div>
      {editing && (
        <section className="card flat" style={{ background: "var(--surface-2)" }}>
          <div className="section-title"><h3>Nouvelle version</h3><span className="spacer" />{(k.active || pending) && <button className="btn sm subtle" onClick={() => setEditing(false)}>Fermer</button>}</div>
          <ProviderForm kind={k.kind} scope={scope} base={latest ?? null} onSaved={() => { setEditing(false); void refresh(); }} />
        </section>
      )}
      {k.history.length > 0 && (
        <section className="stack" style={{ gap: 8 }}>
          <button type="button" className="btn sm subtle" style={{ alignSelf: "flex-start" }} aria-expanded={showHistory} onClick={() => setShowHistory(!showHistory)}><History size={15} />{showHistory ? "Masquer l'historique" : `Historique (${k.history.length})`}</button>
          {showHistory && <div className="card pad-0 flat table-wrap">
            <table className="t">
              <thead><tr><th>Version</th><th>Fournisseur</th><th>État</th><th>Test</th><th>Enregistrée</th><th /></tr></thead>
              <tbody>
                {k.history.map((h) => (
                  <tr key={h.id}>
                    <td><b>v{h.version}</b>{h.note && <div className="tiny muted">{h.note}</div>}</td>
                    <td>{h.provider}<div className="tiny muted">{String(h.config.model ?? h.config.voice ?? h.config.senderAddress ?? h.config.phoneNumberId ?? "")}</div></td>
                    <td><span className={`badge ${h.status === "ACTIVE" ? "b-VALIDATED" : h.status === "INACTIVE" ? "b-IN_REVIEW" : "b-DRAFT"}`}>{STATUS_LABEL[h.status]}</span></td>
                    <td className="small">{h.test ? <span style={{ color: h.test.ok ? "var(--ok)" : "var(--bad)", fontWeight: 700 }}>{h.test.ok ? "Réussi" : "Échoué"}</span> : <span className="muted">—</span>}</td>
                    <td className="tiny muted">{fmtDate(h.createdAt)}</td>
                    <td><div className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                      <button className="btn sm ghost" onClick={() => test.mutate(h.id)}>Tester</button>
                      {canEdit && h.status !== "ACTIVE" && <button className="btn sm ghost" onClick={() => activate.mutate(h.id)}>{h.status === "SUPERSEDED" ? "Revenir" : "Activer"}</button>}
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
        </section>
      )}
    </Drawer>
  );
}

function SettingLine({ s, kind }: { s: ProviderSettingView; kind: ProviderKind }) {
  const def = PROVIDER_CATALOG[kind].providers.find((p) => p.id === s.provider);
  return (
    <div className="kv">
      <div><span>Actif</span><b>{def?.label ?? s.provider}</b> <span className="badge b-VALIDATED" style={{ display: "inline-flex", marginLeft: 4 }}>v{s.version}</span></div>
      {def?.fields.filter((f) => !f.secret && s.config[f.key] !== undefined).map((f) => <div key={f.key}><span>{f.label}</span><code className="mono">{String(s.config[f.key])}</code></div>)}
      {s.secretHints && Object.entries(s.secretHints).map(([key, v]) => <div key={key}><span>{def?.fields.find((f) => f.key === key)?.label ?? key}</span><code className="mono">{v}</code></div>)}
      {s.test && <div><span>Dernier test</span><b style={{ color: s.test.ok ? "var(--ok)" : "var(--bad)" }}>{s.test.ok ? "Réussi" : "Échoué"}</b> <span className="tiny muted">{fmtDate(s.test.at)}</span></div>}
    </div>
  );
}

function ProviderForm({ kind, scope, base, onSaved }: { kind: ProviderKind; scope: string; base: ProviderSettingView | null; onSaved: () => void }) {
  const cat = PROVIDER_CATALOG[kind];
  const { toast } = useStudio();
  const [provider, setProvider] = useState(base?.provider ?? cat.providers[0]!.id);
  const def = cat.providers.find((p) => p.id === provider)!;
  const sameProvider = base?.provider === provider;
  // Valeur reprise de la version précédente, sauf si elle n'est plus dans la liste du champ (ex. une voix retirée du catalogue).
  const initial = (f: ProviderField, previous: unknown) => {
    const v = previous === undefined ? undefined : String(previous);
    return v !== undefined && (!f.options || f.options.some((o) => o.value === v)) ? v : String(f.default ?? "");
  };
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(def.fields.filter((f) => !f.secret).map((f) => [f.key, initial(f, sameProvider ? base?.config[f.key] : undefined)])));
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [problems, setProblems] = useState<string[]>([]);
  const save = useMutation({
    mutationFn: () => admin.saveProvider({ kind, scope, provider, note: note || undefined, secrets, config: Object.fromEntries(Object.entries(values).filter(([, v]) => v !== "").map(([k, v]) => [k, def.fields.find((f) => f.key === k)?.type === "number" ? Number(v) : v])) }),
    onSuccess: (r) => { toast(`Version ${r.version} enregistrée. Testez-la, puis activez-la.`); setSecrets({}); onSaved(); },
    onError: (e) => { const d = e instanceof ApiError ? (e.detail as { problems?: string[] } | undefined) : undefined; setProblems(d?.problems ?? [errorMessage(e)]); },
  });

  function changeProvider(id: string) {
    setProvider(id);
    const d = cat.providers.find((p) => p.id === id)!;
    setValues(Object.fromEntries(d.fields.filter((f) => !f.secret).map((f) => [f.key, initial(f, base?.provider === id ? base.config[f.key] : undefined)])));
    setSecrets({});
  }

  return (
    <form className="stack appear" style={{ gap: 14 }} onSubmit={(e) => { e.preventDefault(); setProblems([]); save.mutate(); }} autoComplete="off">
      {cat.providers.length > 1 && (
        <Field label="Fournisseur">
          <select className="select" value={provider} onChange={(e) => changeProvider(e.target.value)}>{cat.providers.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}</select>
        </Field>
      )}
      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
        {def.fields.map((f) => f.secret ? (
          <Field key={f.key} label={`${f.label}${f.required ? " *" : ""}`} hint={sameProvider && base?.secretHints?.[f.key] ? `Laisser vide pour garder la valeur actuelle (${base.secretHints[f.key]}).` : "Jamais réaffichée après l'enregistrement."}>
            <input className="input mono" type="password" autoComplete="new-password" spellCheck={false} value={secrets[f.key] ?? ""} onChange={(e) => setSecrets({ ...secrets, [f.key]: e.target.value })} placeholder={sameProvider && base?.secretHints?.[f.key] ? base.secretHints[f.key] : ""} />
          </Field>
        ) : (
          <Field key={f.key} label={`${f.label}${f.required ? " *" : ""}`} hint={f.help}>
            {f.options ? (
              <select className="select" value={values[f.key] ?? ""} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}>
                {!f.required && <option value="">—</option>}
                {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <input className="input" type={f.type === "number" ? "number" : "text"} step={f.type === "number" ? "any" : undefined} value={values[f.key] ?? ""} placeholder={f.placeholder} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} />
            )}
          </Field>
        ))}
      </div>
      <Field label="Note (facultatif)" hint="Ex. « nouvelle clé après rotation », visible dans l'historique.">
        <input className="input" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      {problems.length > 0 && <div className="alert err" role="alert">{problems.map((p) => <div key={p}>{p}</div>)}</div>}
      <div className="row">
        {def.docsUrl && <a href={def.docsUrl} target="_blank" rel="noreferrer" className="small">Documentation du fournisseur</a>}
        <span className="spacer" />
        <button className="btn" disabled={save.isPending}>{save.isPending ? "…" : "Enregistrer une nouvelle version"}</button>
      </div>
      <p className="muted small" style={{ margin: 0 }}>L'enregistrement crée une version inactive : rien ne change pour les élèves tant que vous ne l'avez pas activée.</p>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Pays : ouverture à l'inscription, indicatif et format des numéros
// ---------------------------------------------------------------------------
function CountriesTab({ canEdit }: { canEdit: boolean }) {
  const { toast } = useStudio();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["admin-countries"], queryFn: admin.countries });
  const [editing, setEditing] = useState<string | null>(null);
  const [billing, setBilling] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ predicate: (q) => ["admin-countries", "countries", "admin-status"].includes(String(q.queryKey[0])) });
  const setOpen = useMutation({
    mutationFn: async ({ c, open }: { c: AdminCountry; open: boolean }) => {
      try { return await admin.updateCountry(c.id, { isActive: open }); }
      catch (e) {
        const missing = e instanceof ApiError && e.code === "country_not_ready" ? (e.detail as { missing?: string[] }).missing ?? [] : null;
        if (!missing) throw e;
        if (!confirm(`${c.name} n'est pas encore prêt :\n\n• ${missing.join("\n• ")}\n\nOuvrir quand même les inscriptions ?`)) return null;
        return admin.updateCountry(c.id, { isActive: true, force: true });
      }
    },
    onSuccess: (r, v) => { if (r) { toast(v.open ? `${v.c.name} ouvert : visible à la connexion (moins d'une minute)` : `${v.c.name} fermé aux nouvelles inscriptions`); void refresh(); } },
    onError: (e) => toast(errorMessage(e), "err"),
  });

  const setRetention = useMutation({
    mutationFn: ({ c, days }: { c: AdminCountry; days: number }) => admin.updateCountry(c.id, { tutorRetentionDays: days }),
    onSuccess: (_r, v) => { toast(`${v.c.name} : conversations du tuteur gardées ${v.days} jours`); void refresh(); },
    onError: (e) => toast(errorMessage(e), "err"),
  });

  if (list.isLoading) return <Loading />;
  if (list.error) return <ErrorBox error={list.error} onRetry={() => list.refetch()} />;
  return (
    <div className="stack" style={{ gap: 14 }}>
      <PageHead kicker="Administration" title="Pays" lead={<>Un pays <b>ouvert</b> apparaît sur l'écran de connexion de l'app. Fermer un pays bloque les nouvelles inscriptions ; les familles déjà inscrites gardent leur accès.</>} />
      {list.data!.map((c) => {
        const ready = c.readiness.every((x) => x.ok);
        return (
          <div key={c.id} className="card stack appear" style={{ gap: 14 }}>
            <div className="row" style={{ alignItems: "flex-start", gap: 14 }}>
              <span style={{ fontSize: 34, lineHeight: 1 }} aria-hidden>{c.flag}</span>
              <div className="stack" style={{ gap: 4, flex: 1, minWidth: 0 }}>
                <h2 style={{ fontSize: 18 }}>{c.name} <span className="muted small mono">{c.id}</span></h2>
                <div className="row wrap" style={{ gap: 6 }}>
                  <span className="chip static">{c.dialCode ? `+${c.dialCode}` : "indicatif à renseigner"}{c.phoneExample ? ` · ${c.phoneExample}` : ""}</span>
                  <span className="chip static">{c.currency}</span>
                  <span className="chip static">{c.timezone}</span>
                  <span className="chip static">{c.families} famille{c.families > 1 ? "s" : ""}</span>
                  <span className="chip static">Tuteur : {c.tutorRetentionDays} j de conservation</span>
                </div>
              </div>
              <span className={`badge ${c.isActive ? "b-VALIDATED" : "b-DRAFT"}`}>{c.isActive ? "Ouvert" : "Fermé"}</span>
            </div>
            <div className="stack" style={{ gap: 8 }}>
              <div className="row small"><b>Préparation</b><span className="muted">{c.readiness.filter((x) => x.ok).length} / {c.readiness.length}</span></div>
              <div className="progress"><span className="p-ok" style={{ flexGrow: c.readiness.filter((x) => x.ok).length }} /><span style={{ flexGrow: c.readiness.filter((x) => !x.ok).length }} /></div>
              <div className="checklist" aria-label="Préparation du pays">
                {c.readiness.map((x) => (
                  <span key={x.key} className={`badge plain ${x.ok ? "b-VALIDATED" : "b-MISSING"}`}>{x.ok ? <CheckCircle2 size={13} /> : <Circle size={12} />}{x.label}</span>
                ))}
              </div>
            </div>
            <div className="row wrap" style={{ gap: 8, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
              {canEdit && <button className="btn sm ghost" onClick={() => { setBilling(null); setEditing(editing === c.id ? null : c.id); }}>{editing === c.id ? "Fermer" : "Numéros de téléphone"}</button>}
              <button className="btn sm ghost" onClick={() => { setEditing(null); setBilling(billing === c.id ? null : c.id); }} aria-expanded={billing === c.id}>{billing === c.id ? "Masquer les prix" : "Prix et paiement"}</button>
              {canEdit && <button className="btn sm ghost" onClick={() => {
                const v = prompt(`Durée de conservation des conversations du tuteur pour ${c.name} (jours, 7 à 730).\nLes messages plus anciens sont effacés automatiquement.`, String(c.tutorRetentionDays));
                const n = v == null ? NaN : Number(v.trim());
                if (v != null && (!Number.isInteger(n) || n < 7 || n > 730)) { toast("Entre 7 et 730 jours", "err"); return; }
                if (Number.isInteger(n) && n !== c.tutorRetentionDays) setRetention.mutate({ c, days: n });
              }}>Conservation du tuteur</button>}
              <span className="spacer" />
              {canEdit && (c.isActive
                  ? <button className="btn sm danger" onClick={() => { if (confirm(`Fermer ${c.name} aux nouvelles inscriptions ? Les familles inscrites gardent leur accès.`)) setOpen.mutate({ c, open: false }); }}>Fermer aux inscriptions</button>
                  : <button className={`btn sm ${ready ? "" : "amber"}`} onClick={() => setOpen.mutate({ c, open: true })} disabled={!c.dialCode || !c.phonePattern}>{ready ? "Ouvrir le pays" : "Ouvrir (préparation incomplète)"}</button>)}
            </div>
            {editing === c.id && <PhoneForm c={c} onSaved={() => { setEditing(null); void refresh(); }} />}
            {billing === c.id && <BillingPanel c={c} canEdit={canEdit} onChanged={() => void refresh()} />}
          </div>
        );
      })}
      {!canEdit && <span className="muted small">Lecture seule : l'ouverture des pays est réservée à l'administrateur.</span>}
    </div>
  );
}

function PhoneForm({ c, onSaved }: { c: AdminCountry; onSaved: () => void }) {
  const { toast } = useStudio();
  const [dialCode, setDial] = useState(c.dialCode);
  const [pattern, setPattern] = useState(c.phonePattern ?? "");
  const [example, setExample] = useState(c.phoneExample ?? "");
  const exampleOk = (() => { try { const d = example.replace(/\D/g, ""); const re = new RegExp(pattern); return !example || re.test(d) || re.test(d.replace(/^0/, "")); } catch { return false; } })();
  const save = useMutation({ mutationFn: () => admin.updateCountry(c.id, { dialCode, phonePattern: pattern, phoneExample: example }), onSuccess: () => { toast("Format des numéros enregistré"); onSaved(); }, onError: (e) => toast(errorMessage(e), "err") });
  return (
    <form className="stack appear" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 12, padding: 14 }} onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <div className="grid" style={{ gridTemplateColumns: "140px 1fr 1fr" }}>
        <Field label="Indicatif"><input className="input mono" value={dialCode} onChange={(e) => setDial(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="221" required /></Field>
        <Field label="Format du numéro national" hint="Expression régulière, sans l'indicatif. Ex. ^[37]\d{8}$"><input className="input mono" value={pattern} onChange={(e) => setPattern(e.target.value)} required /></Field>
        <Field label="Exemple affiché" hint={exampleOk ? "Affiché comme modèle sur l'écran de connexion." : "L'exemple ne respecte pas le format."}><input className="input mono" value={example} onChange={(e) => setExample(e.target.value)} style={exampleOk ? undefined : { borderColor: "var(--bad)" }} /></Field>
      </div>
      <div className="row"><span className="muted small">Aperçu : <b>{`${c.flag} +${dialCode || "…"}`}</b> <span className="mono">{example}</span></span><span className="spacer" /><button className="btn" disabled={save.isPending || !exampleOk || !dialCode || !pattern}>Enregistrer</button></div>
    </form>
  );
}

/**
 * Prix des formules et moyens de paiement d'un pays. Un nouveau prix vaut pour les prochains paiements :
 * un paiement déjà lancé garde son montant. Un moyen sans connecteur ne peut pas être proposé en production.
 */
function BillingPanel({ c, canEdit, onChanged }: { c: AdminCountry; canEdit: boolean; onChanged: () => void }) {
  const { toast } = useStudio();
  const qc = useQueryClient();
  const key = ["admin-billing", c.id];
  const b = useQuery({ queryKey: key, queryFn: () => admin.billing(c.id) });
  const done = (msg: string) => { toast(msg); void qc.invalidateQueries({ queryKey: key }); onChanged(); };
  const setPrice = useMutation({
    mutationFn: (x: { planId: string; amount: number; isActive: boolean }) => admin.setPlanPrice(c.id, x.planId, { amount: x.amount, isActive: x.isActive }),
    onSuccess: (r) => done(r.isActive ? "Prix enregistré : visible au prochain paiement" : "Formule retirée de l'offre"),
    onError: (e) => toast(errorMessage(e), "err"),
  });
  const setProvider = useMutation({
    mutationFn: (x: { id: string; isActive?: boolean; priority?: number }) => admin.setPaymentProvider(c.id, x.id, { isActive: x.isActive, priority: x.priority }),
    onSuccess: () => done("Moyens de paiement mis à jour"),
    onError: (e) => toast(errorMessage(e), "err"),
  });

  if (b.isLoading) return <Loading />;
  if (b.error) return <ErrorBox error={b.error} onRetry={() => b.refetch()} />;
  const d = b.data!;
  const move = (i: number, dir: -1 | 1) => {
    const list = [...d.providers]; const j = i + dir; if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j]!, list[i]!];
    // Réécrit l'ordre complet (priorités 1..n) : simple et sans ex æquo.
    list.forEach((p, k) => { if (p.priority !== k + 1) setProvider.mutate({ id: p.id, priority: k + 1 }); });
  };
  return (
    <div className="stack appear" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, gap: 18 }}>
      <div className="stack" style={{ gap: 8 }}>
        <h3 style={{ margin: 0 }}>Formules ({d.currency})</h3>
        {d.plans.map((p) => <PlanRow key={p.planId} p={p} currency={d.currency} canEdit={canEdit} busy={setPrice.isPending} onSave={(amount, isActive) => setPrice.mutate({ planId: p.planId, amount, isActive })} />)}
        <p className="muted small" style={{ margin: 0 }}>Un nouveau prix s'applique aux prochains paiements ; les abonnements en cours ne changent pas.</p>
      </div>
      <div className="stack" style={{ gap: 8 }}>
        <h3 style={{ margin: 0 }}>Moyens de paiement</h3>
        {d.providers.map((p, i) => (
          <div key={p.id} className="row" style={{ gap: 8 }}>
            {canEdit && <span className="row" style={{ gap: 2 }}>
              <button className="btn sm ghost" aria-label={`Monter ${p.label}`} disabled={i === 0 || setProvider.isPending} onClick={() => move(i, -1)}>↑</button>
              <button className="btn sm ghost" aria-label={`Descendre ${p.label}`} disabled={i === d.providers.length - 1 || setProvider.isPending} onClick={() => move(i, 1)}>↓</button>
            </span>}
            <b>{p.label}</b>
            {!p.supported && <span className="badge b-DRAFT">Connecteur à venir</span>}
            {p.supported && !p.keysConfigured && <span className="badge b-IN_REVIEW">Clés à configurer</span>}
            <span className="spacer" />
            {canEdit
              ? <label className="row" style={{ gap: 6 }}><input type="checkbox" checked={p.isActive} disabled={setProvider.isPending} onChange={(e) => setProvider.mutate({ id: p.id, isActive: e.target.checked })} /> Proposé aux parents</label>
              : <span className="muted small">{p.isActive ? "Proposé" : "Non proposé"}</span>}
          </div>
        ))}
        <p className="muted small" style={{ margin: 0 }}>L'ordre est celui de l'écran de paiement. Les clés Wave, Orange Money et Bictorys se règlent dans l'onglet Fournisseurs.</p>
      </div>
      {!canEdit && <span className="muted small">Lecture seule : les prix sont réservés à l'administrateur.</span>}
    </div>
  );
}

function PlanRow({ p, currency, canEdit, busy, onSave }: { p: CountryBilling["plans"][number]; currency: string; canEdit: boolean; busy: boolean; onSave: (amount: number, isActive: boolean) => void }) {
  const [amount, setAmount] = useState(String(p.amount || ""));
  useEffect(() => setAmount(String(p.amount || "")), [p.amount]);
  const n = Number(amount || 0);
  const dirty = n !== p.amount;
  // Une formule sans prix n'est jamais montrée aux parents, quel que soit son interrupteur.
  const offered = p.isActive && p.amount > 0;
  const fmt = (x: number) => `${x.toLocaleString("fr-FR")} ${currency}`;
  return (
    <form className="row" style={{ gap: 8 }} onSubmit={(e) => { e.preventDefault(); onSave(n, p.isActive && n > 0); }}>
      <span style={{ width: 200, flexShrink: 0 }}><b>{p.label}</b> <span className="muted small">{p.isExamPass ? "pass examen" : `${p.months} mois`}</span></span>
      {canEdit
        ? <input className="input mono" inputMode="numeric" aria-label={`Prix ${p.label}`} style={{ width: 120 }} value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, "").slice(0, 8))} placeholder="0" />
        : <span className="mono">{p.amount ? fmt(p.amount) : "—"}</span>}
      {canEdit && dirty && <button className="btn sm" disabled={busy}>Enregistrer</button>}
      <span className="spacer" />
      {canEdit
        ? <label className="row" style={{ gap: 6 }} title={n > 0 ? undefined : "Définissez un prix d'abord"}><input type="checkbox" checked={offered} disabled={busy || (!offered && n <= 0)} onChange={(e) => onSave(n, e.target.checked)} /> {n > 0 ? "Proposée" : "Sans prix"}</label>
        : <span className="muted small">{offered ? "Proposée" : "Non proposée"}</span>}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Équipe
// ---------------------------------------------------------------------------
function TeamTab() {
  const { teacher, toast } = useStudio();
  const qc = useQueryClient();
  const team = useQuery({ queryKey: ["admin-team"], queryFn: admin.team });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-team"] });
  const update = useMutation({ mutationFn: ({ id, b }: { id: string; b: Parameters<typeof admin.updateMember>[1] }) => admin.updateMember(id, b), onSuccess: () => void refresh(), onError: (e) => toast(errorMessage(e), "err") });
  const revoke = useMutation({ mutationFn: admin.revokeSessions, onSuccess: () => toast("Sessions coupées : reconnexion obligatoire") });
  const reset = useMutation({ mutationFn: admin.resetTotp, onSuccess: () => { toast("Double authentification réinitialisée"); void refresh(); }, onError: (e) => toast(errorMessage(e), "err") });
  const unlinkTg = useMutation({ mutationFn: admin.unlinkTelegram, onSuccess: () => { toast("Appairage Telegram retiré : codes par SMS"); void refresh(); }, onError: (e) => toast(errorMessage(e), "err") });
  const [adding, setAdding] = useState(false);

  if (team.isLoading) return <Loading />;
  if (team.error) return <ErrorBox error={team.error} onRetry={() => team.refetch()} />;

  const toggleRole = (m: TeamMember, r: Role) => {
    const roles = m.roles.includes(r) ? m.roles.filter((x) => x !== r) : [...m.roles, r];
    if (!roles.length) return toast("Au moins un rôle", "err");
    update.mutate({ id: m.id, b: { roles } });
  };

  return (
    <div className="stack" style={{ gap: 14 }}>
      <PageHead kicker="Administration" title="Équipe" lead={<>Les rôles <b>contenu</b> donnent accès aux leçons. Les rôles <b>système</b> donnent accès à l'administration, avec double authentification.</>}>
        <button className="btn" onClick={() => setAdding(!adding)}>{adding ? "Fermer" : <><Plus size={15} />Ajouter un membre</>}</button>
      </PageHead>
      {adding && <AddMember onDone={() => { setAdding(false); void refresh(); }} />}
      <TelegramPhones />
      <div className="card pad-0 table-wrap">
        <table className="t">
          <thead><tr><th>Membre</th><th>Rôles contenu</th><th>Rôles système</th><th>2FA</th><th>Actif</th><th /></tr></thead>
          <tbody>
            {team.data!.map((m) => (
              <tr key={m.id} style={{ opacity: m.isActive ? 1 : 0.55 }}>
                <td><div className="row" style={{ gap: 10 }}><Avatar name={m.displayName} small /><div><b>{m.displayName}</b>{m.id === teacher?.id && <span className="small muted"> (vous)</span>}<div className="tiny muted mono">{m.phone}</div></div></div></td>
                {(["content", "system"] as const).map((fam) => (
                  <td key={fam}>
                    <div className="step-sel">
                      {STUDIO_ROLES.filter((r) => ROLE_INFO[r].family === fam).map((r) => (
                        <button key={r} type="button" className="chip" title={ROLE_INFO[r].description} aria-pressed={m.roles.includes(r)} onClick={() => toggleRole(m, r)}
                          style={m.roles.includes(r) && fam === "system" ? { background: "var(--info-soft)", color: "var(--info)", borderColor: "transparent" } : undefined}>
                          {ROLE_INFO[r].label}
                        </button>
                      ))}
                    </div>
                  </td>
                ))}
                <td className="small">{m.roles.some((r) => ROLE_INFO[r].family === "system") ? (m.totpEnabled ? <span style={{ color: "var(--ok)" }}>Activée</span> : <span style={{ color: "var(--amber-text)" }}>À activer</span>) : <span className="muted">—</span>}</td>
                <td><input type="checkbox" className="switch" aria-label={`Compte de ${m.displayName} actif`} checked={m.isActive} onChange={(e) => update.mutate({ id: m.id, b: { isActive: e.target.checked } })} /></td>
                <td><div className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                  <button className="btn sm ghost" onClick={() => revoke.mutate(m.id)}>Couper les sessions</button>
                  {m.totpEnabled && m.id !== teacher?.id && <button className="btn sm danger" onClick={() => { if (confirm(`Réinitialiser la double authentification de ${m.displayName} (téléphone perdu) ?`)) reset.mutate(m.id); }}>Réinitialiser la 2FA</button>}
                  {m.telegramLinked && <button className="btn sm ghost" title="Ses codes de connexion repartiront par SMS" onClick={() => { if (confirm(`Retirer l'appairage Telegram de ${m.displayName} ?`)) unlinkTg.mutate(m.id); }}><Send size={14} />Retirer Telegram</button>}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Numéros de l'app (parents) dont les codes de connexion partent par Telegram au lieu du SMS : numéros de test et
 * support, quand le fournisseur de SMS ne passe pas. Réservé à l'administrateur, journalisé.
 */
function TelegramPhones() {
  const { toast } = useStudio();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["admin-telegram-phones"], queryFn: admin.telegramPhones, refetchInterval: 5000 });
  const [phone, setPhone] = useState("+221");
  const [link, setLink] = useState<{ phone: string; url: string } | null>(null);
  const issue = useMutation({
    mutationFn: () => admin.telegramPhoneLink(phone.replace(/\s/g, "")),
    onSuccess: (r) => { setLink({ phone, url: r.url }); window.open(r.url, "_blank", "noopener"); },
    onError: (e) => toast(errorMessage(e), "err"),
  });
  const remove = useMutation({ mutationFn: admin.telegramPhoneUnlink, onSuccess: () => { toast("Numéro retiré : ses codes repartent par SMS"); void qc.invalidateQueries({ queryKey: ["admin-telegram-phones"] }); }, onError: (e) => toast(errorMessage(e), "err") });
  return (
    <div className="card stack" style={{ gap: 12 }}>
      <div className="stack" style={{ gap: 2 }}>
        <h2 style={{ fontSize: 17 }}>Codes de l'app par Telegram (numéros de test)</h2>
        <span className="small muted">Quand le SMS ne passe pas, un numéro de parent peut recevoir ses codes de connexion dans Telegram. Générez le lien, ouvrez-le sur le Telegram qui doit recevoir les codes, touchez Démarrer.</span>
      </div>
      <form className="row wrap" style={{ gap: 8, alignItems: "flex-end" }} onSubmit={(e) => { e.preventDefault(); if (!issue.isPending && /^\+\d{8,15}$/.test(phone.replace(/\s/g, ""))) issue.mutate(); }}>
        <Field label="Numéro (international)"><input className="input mono" style={{ width: 200 }} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+221 77 000 00 00" /></Field>
        <button className="btn sm" disabled={issue.isPending}><Send size={14} />Générer le lien</button>
        {link && <span className="small muted">Lien ouvert pour {link.phone} (10 min). <a href={link.url} target="_blank" rel="noreferrer noopener">Rouvrir</a></span>}
      </form>
      {(list.data?.length ?? 0) > 0 && (
        <div className="row wrap" style={{ gap: 8 }}>
          {list.data!.map((x) => (
            <span key={x.phoneE164} className="chip static" style={{ gap: 8 }}>{x.phone}
              <button type="button" className="icon-btn" style={{ width: 22, height: 22 }} aria-label={`Retirer ${x.phone}`} onClick={() => { if (confirm(`Retirer ${x.phone} ? Ses codes repartiront par SMS.`)) remove.mutate(x.phoneE164); }}><XCircle size={14} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function AddMember({ onDone }: { onDone: () => void }) {
  const { toast, languages } = useStudio();
  const [phone, setPhone] = useState("+221");
  const [name, setName] = useState("");
  const [roles, setRoles] = useState<Role[]>(["AUTHOR"]);
  const [langs, setLangs] = useState<string[]>([]);
  const add = useMutation({ mutationFn: () => admin.addMember({ phone: phone.replace(/\s/g, ""), displayName: name, roles, languages: langs }), onSuccess: () => { toast("Membre ajouté : il se connecte avec son numéro"); onDone(); }, onError: (e) => toast(errorMessage(e), "err") });
  return (
    <form className="card stack appear" onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
      <div className="grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <Field label="Téléphone (format international)"><input className="input mono" value={phone} onChange={(e) => setPhone(e.target.value)} pattern="\+\d{8,15}" required /></Field>
        <Field label="Nom affiché"><input className="input" value={name} onChange={(e) => setName(e.target.value)} minLength={2} maxLength={60} required /></Field>
      </div>
      <Field label="Rôles">
        <div className="step-sel">
          {STUDIO_ROLES.map((r) => <button key={r} type="button" className="chip" aria-pressed={roles.includes(r)} title={ROLE_INFO[r].description} onClick={() => setRoles(roles.includes(r) ? roles.filter((x) => x !== r) : [...roles, r])}>{ROLE_INFO[r].label}</button>)}
        </div>
      </Field>
      {roles.includes("LANGUAGE_REVIEWER") && (
        <Field label="Langues relues">
          <div className="step-sel">{languages.filter((l) => l.code !== "fr").map((l) => <button key={l.code} type="button" className="chip" aria-pressed={langs.includes(l.code)} onClick={() => setLangs(langs.includes(l.code) ? langs.filter((x) => x !== l.code) : [...langs, l.code])}>{l.name}</button>)}</div>
        </Field>
      )}
      <div className="row"><span className="spacer" /><button className="btn" disabled={add.isPending || !roles.length}>Ajouter</button></div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Journal
// ---------------------------------------------------------------------------
const ACTION: Record<string, string> = {
  "provider.save": "Réglage enregistré", "provider.activate": "Réglage activé", "provider.deactivate": "Réglage désactivé", "provider.test": "Test de connexion",
  "secrets.reencrypt": "Clés re-chiffrées", "totp.enable": "Double authentification activée", "team.create": "Membre ajouté", "team.update": "Membre modifié",
  "team.revoke_sessions": "Sessions coupées", "country.open": "Pays ouvert", "country.close": "Pays fermé", "country.update": "Pays modifié", "team.reset_totp": "2FA réinitialisée", "team.grant_admin_cli": "Administrateur créé (serveur)",
  "billing.price": "Prix modifié", "billing.provider": "Moyen de paiement modifié",
  "access.update": "Formule modifiée", "access.reset": "Formule : valeur héritée rétablie",
};

function AuditTab() {
  const rows = useQuery({ queryKey: ["admin-audit"], queryFn: () => admin.audit(100) });
  if (rows.isLoading) return <Loading />;
  if (rows.error) return <ErrorBox error={rows.error} onRetry={() => rows.refetch()} />;
  return (
    <>
    <PageHead kicker="Administration" title="Journal" lead="Les 100 dernières actions d'administration : réglages, activations, équipe, prix." />
    <div className="card pad-0 table-wrap">
      <table className="t">
        <thead><tr><th>Quand</th><th>Qui</th><th>Action</th><th>Sur</th><th>Détail</th></tr></thead>
        <tbody>
          {rows.data!.map((x) => (
            <tr key={x.id}>
              <td className="small muted" title={fmtDate(x.createdAt)} style={{ whiteSpace: "nowrap" }}>{timeAgo(x.createdAt)}</td>
              <td><span className="row" style={{ gap: 8 }}><Avatar name={x.who} small />{x.who}</span></td>
              <td><b>{ACTION[x.action] ?? x.action}</b></td>
              <td className="mono small">{x.target ?? "—"}</td>
              <td className="small muted mono">{x.detail ? Object.entries(x.detail).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`).join(" · ") : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.data!.length === 0 && <Empty title="Aucune action pour l'instant." />}
    </div>
    </>
  );
}

export const canSeeSystem = (roles: readonly string[]) => can(roles, "system.view");
